import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { NitroFetchOptions } from 'nitropack';
import { useCustomFetch } from '~/composables';
import type {
    ComposableOptionsBase,
    ContentComponentType,
    ContentMetaResponse,
    GetContentDataResponse,
    PaginatedRequest
} from '~/types';
import { trim as lo_trim } from 'es-toolkit';
import { isError as lo_isError } from 'es-toolkit';
import { isString as lo_isString } from 'es-toolkit';
import type { NuxtError } from '#app';

export interface UseContentOptions extends ComposableOptionsBase {}
export function useContent(options: Partial<UseContentOptions> = {}) {
    const apiBasePath = `/v1/ct`;
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });

    async function getContentMetadata(
        route: RouteLocationNormalizedLoaded | string,
        args: PaginatedRequest = {}
    ): Promise<ContentMetaResponse | Error> {
        const { path, query } = lo_isString(route) ? { path: route, query: args?.params ?? {} } : route;
        const url = getContentApiPath(path);
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query
        };

        try {
            return await $_fetch<ContentMetaResponse>(url, config);
        } catch (err) {
            return err as NuxtError;
        }
    }

    async function getPageContent(routeObj: RouteLocationNormalizedLoaded): Promise<GetContentDataResponse | NuxtError | Error | void> {
        const dynamicPageData: ContentMetaResponse | Error = await getContentMetadata(routeObj);

        if (lo_isError(dynamicPageData)) {
            return dynamicPageData;
        }

        if (!dynamicPageData?.meta) {
            return new Error('No metadata found for this path');
        }

        let { content, meta } = dynamicPageData;
        let baseComponent: ContentComponentType | null = null

        switch (meta.type) {
            case 'page':
                baseComponent = 'TypePage';
                break;
            case 'market':
            case 'category':
            case 'collection':
                baseComponent = 'TypeCollection';
                break;
            case 'article':
                baseComponent = 'TypeArticle';
                break;
            case 'product':
                baseComponent = 'TypeProduct';
                break;
            case 'redirect':
                baseComponent = 'None';
                break;
        }

        return {
            content,
            meta,
            baseComponent
        };
    }

    function getContentApiPath(path: string): string {
        const urlPath = lo_trim(path, '/');
        return `${apiBasePath}/${urlPath}/`;
    }

    return {
        getContentMetadata,
        getPageContent,
        getContentApiPath
    };
}
